import axios, {
  AxiosError,
  AxiosHeaders,
  AxiosRequestConfig,
  AxiosResponse,
} from 'axios';
import { UAParser } from 'ua-parser-js';

import { CookieService } from './cookieService';

const validOS = ['windows', 'linux', 'macos', 'android', 'ios'];
const osName = UAParser().os.name?.toLowerCase().replace(/\s/g, '');

const isValidOS = typeof osName === 'string' && validOS.includes(osName);
const clientOS = (() => {
  if (isValidOS) {
    return osName;
  }
  return 'windows';
})();

const clientBrowserName = UAParser().browser.name;
const clientBrowserVersion = UAParser().browser.version;

export const ADDITIONAL_CLIENT_HEADERS = {
  'X-Client-OS': String(clientOS),
  'X-Client-Device': String(clientBrowserName),
  'X-Client-Version': String(clientBrowserVersion),
};

const API_URL = process.env['NX_API_URL'] || '';

interface RefreshTokenResponse {
  token: string;
  refresh_token: string;
}

/**
 * Error types for better debugging and monitoring
 */
type RefreshTokenErrorType =
  | 'NETWORK_ERROR'
  | 'INVALID_TOKEN'
  | 'SERVER_ERROR'
  | 'NO_REFRESH_TOKEN';

/**
 * Simple error logging utility
 */
function logRefreshError(
  errorType: RefreshTokenErrorType,
  message: string,
  originalError?: unknown
) {
  console.error(`[Token Refresh] ${errorType}:`, message, originalError);
}

/**
 * Simple token manager to handle refresh token logic
 */
class TokenManager {
  private isRefreshing = false;
  private refreshPromise: Promise<string | null> | null = null;

  async refreshToken(): Promise<string | null> {
    if (this.isRefreshing && this.refreshPromise) {
      return this.refreshPromise;
    }

    this.isRefreshing = true;
    this.refreshPromise = this.performRefresh();

    try {
      const result = await this.refreshPromise;
      return result;
    } finally {
      this.isRefreshing = false;
      this.refreshPromise = null;
    }
  }

  private async performRefresh(): Promise<string | null> {
    try {
      const refreshToken = CookieService.getRefreshToken();
      if (!refreshToken) {
        logRefreshError(
          'NO_REFRESH_TOKEN',
          'No refresh token available in cookies'
        );
        return null;
      }

      // Determine subscriber based on current URL/domain
      const isAdmin =
        typeof window !== 'undefined' &&
        (window.location.hostname.includes('admin') ||
          window.location.pathname.includes('admin') ||
          window.location.host.includes(':4300'));
      const subscriber = isAdmin ? 'web_admin' : 'web_buyer';

      const { data } = await axios.post<RefreshTokenResponse>(
        `${API_URL}/auth/refresh-token`,
        { refresh_token: refreshToken, subscriber },
        {
          headers: {
            ...ADDITIONAL_CLIENT_HEADERS,
            'Content-Type': 'application/json',
          },
        }
      );

      CookieService.setAuthTokens(data.token, data.refresh_token);
      console.log('[Token Refresh] Success: New token obtained');
      return data.token;
    } catch (error) {
      // Handle different error types for better debugging
      if (error instanceof AxiosError) {
        if (!error.response) {
          logRefreshError(
            'NETWORK_ERROR',
            'Network error during token refresh',
            error
          );
        } else if (error.response.status === 401) {
          logRefreshError(
            'INVALID_TOKEN',
            'Refresh token is invalid or expired',
            error
          );
          // Only redirect to login on invalid token
          if (typeof window !== 'undefined') {
            window.location.href = '/login';
          }
        } else {
          logRefreshError(
            'SERVER_ERROR',
            `Server error: ${error.response.status}`,
            error
          );
        }
      } else {
        logRefreshError(
          'SERVER_ERROR',
          'Unknown error during token refresh',
          error
        );
      }

      // Always clear cookies on any refresh failure
      CookieService.clearAllAuthCookies();

      // Return null instead of throwing - prevents infinite loops
      return null;
    }
  }
}

// Single instance of token manager
const tokenManager = new TokenManager();

/**
 * Handles request errors with consistent logging
 */
function handleRequestError(error: AxiosError): Promise<never> {
  console.error('[API Request Error]:', {
    message: error.message,
    code: error.code,
    url: error.config?.url,
    method: error.config?.method,
  });
  return Promise.reject(error);
}

/**
 * Handles successful responses
 */
function handleSuccessResponse(response: AxiosResponse): AxiosResponse {
  return response;
}

/**
 * Handles 401 unauthorized errors with token refresh
 */
async function handleUnauthorizedError(
  error: AxiosError,
  axiosInstance: ReturnType<typeof axios.create>
): Promise<AxiosResponse | never> {
  const originalRequest = error.config;

  if (
    error.response?.status === 401 &&
    originalRequest &&
    !originalRequest._retry
  ) {
    // Mark this request as being retried to prevent infinite loops
    originalRequest._retry = true;

    console.log(
      '[401 Handler] Attempting token refresh for:',
      originalRequest.url
    );

    const newToken = await tokenManager.refreshToken();

    if (newToken) {
      // Update the authorization header and retry the request
      if (!originalRequest.headers) {
        originalRequest.headers = new AxiosHeaders();
      }
      originalRequest.headers.Authorization = `Bearer ${newToken}`;

      console.log('[401 Handler] Token refreshed, retrying request');
      return axiosInstance(originalRequest);
    } else {
      console.log('[401 Handler] Token refresh failed, rejecting request');
    }
  }

  return Promise.reject(error);
}

/**
 * Handles response errors with consistent logging
 */
async function handleResponseError(
  error: AxiosError,
  axiosInstance: ReturnType<typeof axios.create>
): Promise<AxiosResponse | never> {
  // Log non-401 errors for debugging
  if (error.response?.status !== 401) {
    console.error('[API Response Error]:', {
      status: error.response?.status,
      statusText: error.response?.statusText,
      url: error.config?.url,
      method: error.config?.method,
    });
  }

  if (error.response?.status === 401) {
    return handleUnauthorizedError(error, axiosInstance);
  }

  return Promise.reject(error);
}

function getContentType(config?: AxiosRequestConfig<unknown>) {
  const contentType: Record<string, string> =
    (config?.method ?? '').toUpperCase() === 'DELETE'
      ? {}
      : { 'Content-Type': 'application/json' };
  return contentType;
}

function getTimeoutConfig(): AxiosRequestConfig {
  return {
    timeout: 120_000,
  };
}

/**
 * Gets the best available token (cookie first, then fallback)
 */
function getBestToken(fallbackToken?: string | null): string | null {
  if (typeof window === 'undefined') return fallbackToken || null;

  const cookieToken = CookieService.getJwtToken();
  const isLoginPage = window.location.pathname === '/login';
  if (isLoginPage) return fallbackToken || null;

  return cookieToken || fallbackToken || null;
}

/**
 * Creates an API instance with automatic token refresh
 *
 * @param token Bearer token (optional)
 * @param config AxiosRequestConfig (optional)
 * @param baseURL Additional base url options (optional)
 */
export function apiInstance(
  token?: string | null,
  config?: AxiosRequestConfig<unknown>,
  baseURL = API_URL
) {
  const axiosInstance = axios.create({
    baseURL,
    headers: {
      ...ADDITIONAL_CLIENT_HEADERS,
      ...config?.headers,
      ...getContentType(config),
    },
    ...getTimeoutConfig(),
    ...config,
  });

  axiosInstance.interceptors.request.use((config) => {
    const tokenToUse = getBestToken(token);
    if (tokenToUse) {
      config.headers.Authorization = `Bearer ${tokenToUse}`;
    }
    return config;
  }, handleRequestError);

  axiosInstance.interceptors.response.use(
    handleSuccessResponse,
    (error: AxiosError) => handleResponseError(error, axiosInstance)
  );

  return axiosInstance;
}

declare module 'axios' {
  interface AxiosRequestConfig {
    _retry?: boolean;
  }
}
